/**
 * UrenPage — Hours dashboard with three views: Periode, Week, Dag
 * Periode: pay period (20e-19e) per staff — surcharges, overtime, leave
 * Week: ma-zo grid — staff × days with hours per cell
 * Dag: single date — all clock entries with in/out/break/netto
 */

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, addDays, getWeek } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Card, Spinner } from '../components/ui';
import { TimeEntryEditDialog } from '../components/uren/TimeEntryEditDialog';
import { ShiftTypeSelector } from '../components/uren/ShiftTypeSelector';
import { PauzeRegelsModal } from '../components/uren/PauzeRegelsModal';
import { NmbrsExportPreview } from '../components/uren/NmbrsExportPreview';
import { useWorkforceStore } from '../stores';
import { supabase } from '../lib/supabase';
import { apiFetch, type Staff, type TimeEntry, type LeaveEntry, type BreakSource } from '../stores/workforce-store';
import type { ScheduleEntry } from '../stores/schedule-store';
import type { ShiftTypeValue } from '../lib/shift-labels';
import {
  getPayPeriod,
  calculateSurcharges,
  calculateOvertime,
  calculateScheduledHours,
  calculateYearSaldo,
  formatHours,
  formatEuro,
  type PayPeriod,
} from '../lib/hours-calculator';
import { filterSickLeave, filterNonSickLeave } from '../lib/leave-types';
import { calculatePotjeSaldo, type Mutation } from '../lib/urensaldo-calculator';

type ViewMode = 'periode' | 'week' | 'dag';

function toHHmm(val: string | null | undefined): string | null {
  if (!val) return null;
  if (val.includes('T')) {
    const d = new Date(val);
    if (isNaN(d.getTime())) return null;
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return val.slice(0, 5);
}

function getMonday(d: Date): Date {
  const day = d.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + offset);
  return monday;
}

function getWeekDates(monday: Date): string[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return fmtDate(d);
  });
}

function fmtDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const BREAK_SOURCE_META: Record<BreakSource, { color: string; label: string }> = {
  schedule: { color: 'bg-emerald-500', label: 'Auto (uit planning)' },
  team_fallback: { color: 'bg-amber-400', label: 'Auto (team fallback — geen planning)' },
  manual: { color: 'bg-indigo-500', label: 'Handmatig gezet' },
  clocked: { color: 'bg-purple-500', label: 'Geklokt door medewerker' },
};

function BreakSourceDot({ source }: { source: BreakSource | null | undefined }) {
  if (!source) return null;
  const meta = BREAK_SOURCE_META[source];
  if (!meta) return null;
  return (
    <span
      aria-label={meta.label}
      title={meta.label}
      className={`ml-1 inline-block h-1.5 w-1.5 rounded-full align-middle ${meta.color}`}
    />
  );
}

// ────────────────────────────────────────────────────────────────
// Periode view (existing logic)
// ────────────────────────────────────────────────────────────────

function PeriodeStaffRow({ staff, timeEntries, leaveEntries, period, potjeSaldo, onClick }: {
  staff: Staff; timeEntries: TimeEntry[]; leaveEntries: LeaveEntry[]; period: PayPeriod; potjeSaldo: number | null; onClick: () => void;
}) {
  const isZzp = staff.contract_type === 'zzp';
  let workedHours = 0;
  let s30 = 0, s50 = 0, s150 = 0;
  let h30 = 0, h50 = 0, h150 = 0;
  for (const te of timeEntries) {
    if (te.total_hours) workedHours += te.total_hours;
    if (te.clock_in && te.clock_out && staff.uurloon) {
      const clockIn = toHHmm(te.clock_in);
      const clockOut = toHHmm(te.clock_out);
      if (clockIn && clockOut) {
        const sc = calculateSurcharges(clockIn, clockOut, te.date, staff.uurloon, te.break_minutes ?? 0, te.paid_break_minutes ?? 0);
        for (const b of sc.breakdown) {
          if (b.percentage === 0.30) { s30 += b.amount; h30 += b.hours; }
          else if (b.percentage === 0.50) { s50 += b.amount; h50 += b.hours; }
          else if (b.percentage === 1.50) { s150 += b.amount; h150 += b.hours; }
        }
      }
    }
  }
  const overtime = calculateOvertime(workedHours);
  const leaveHours = filterNonSickLeave(leaveEntries).reduce((sum, l) => sum + l.hours, 0);
  const sickHours = filterSickLeave(leaveEntries).reduce((sum, l) => sum + l.hours, 0);

  // Scheduled hours (verwacht) — only for vast with werkrooster (not min-max: they structurally work above minimum)
  const isVastWithRooster = staff.contract_type === 'vast' && staff.werkrooster && staff.werkrooster.length > 0;
  const scheduled = isVastWithRooster
    ? calculateScheduledHours(staff.werkrooster, staff.team, period.from, period.to, timeEntries, leaveEntries.map(l => ({ date: l.date, hours: l.hours, leave_type: l.leave_type })), staff.datum_in_dienst || staff.start_date || null)
    : null;

  // Brutoloon: vast = maand_norm × uurloon + toeslagen, overig = gewerkte uren × uurloon + toeslagen
  const uurloon = staff.uurloon || 0;
  const isVast = staff.contract_type === 'vast';
  const isVastOrMinMax = staff.contract_type === 'vast' || staff.contract_type === 'min-max';
  const contractUrenPerMaand = isVast && staff.uren_per_week
    ? Math.round((staff.uren_per_week * 52 / 12) * 100) / 100
    : null;
  const totalSurcharges = s30 + s50 + s150;
  const baseHours = contractUrenPerMaand ?? (isVastOrMinMax ? (staff.maand_norm || 173) : (overtime.paidHours + leaveHours + sickHours));
  const brutoloon = !isZzp && uurloon > 0
    ? baseHours * uurloon + totalSurcharges
    : 0;

  return (
    <tr onClick={onClick} className="hover:bg-gray-50 cursor-pointer">
      <td className="px-5 py-3">
        <p className="font-medium text-main">{staff.name}</p>
        <p className="text-[10px] text-muted capitalize">{staff.team || '-'} · {staff.contract_type}</p>
      </td>
      <td className="px-3 py-3 text-right">
        <span className="text-sm text-muted">{scheduled ? formatHours(scheduled.totalScheduled) : <span className="text-muted">-</span>}</span>
      </td>
      <td className="px-3 py-3 text-right"><span className="text-sm font-semibold text-main">{formatHours(workedHours)}</span></td>
      <td className="px-3 py-3 text-right"><span className="text-sm text-amber-600">{leaveHours > 0 ? formatHours(leaveHours) : '-'}</span></td>
      <td className="px-3 py-3 text-right"><span className="text-sm text-red-600">{sickHours > 0 ? formatHours(sickHours) : '-'}</span></td>
      <td className="px-3 py-3 text-right">
        {scheduled ? (
          <span className={`text-sm font-medium ${scheduled.saldo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {scheduled.saldo >= 0 ? '+' : ''}{formatHours(scheduled.saldo)}
          </span>
        ) : <span className="text-sm text-muted">-</span>}
      </td>
      <td className="px-3 py-3 text-right" title="Overurenpotje — cumulatieve mutaties uit NMBRS-exports">
        {potjeSaldo !== null ? (
          <span className={`text-sm font-semibold ${potjeSaldo >= 0 ? 'text-indigo-600' : 'text-red-600'}`}>
            {potjeSaldo >= 0 ? '+' : ''}{formatHours(potjeSaldo)}
          </span>
        ) : <span className="text-sm text-muted">-</span>}
      </td>
      <td className="px-3 py-3 text-right"><span className="text-sm font-semibold text-main">{!isZzp && brutoloon > 0 ? formatEuro(brutoloon) : '-'}</span></td>
    </tr>
  );
}

// ────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────

export function UrenPage() {
  const {
    staffList, timeEntries, leaveEntries, leaveEntitlements,
    isLoadingEntries, isLoadingStaff,
    fetchStaff, fetchTimeEntries, fetchLeave, fetchEntitlements,
  } = useWorkforceStore();

  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('dag');
  const [currentPeriod, setCurrentPeriod] = useState<PayPeriod>(() => getPayPeriod(new Date()));
  const [weekStart, setWeekStart] = useState<Date>(() => getMonday(new Date()));
  const [selectedDate, setSelectedDate] = useState<string>(fmtDate(new Date()));
  const [syncResult, setSyncResult] = useState<{ updated: number; details: string[] } | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [yearTimeEntries, setYearTimeEntries] = useState<TimeEntry[]>([]);
  const [yearLeaveEntries, setYearLeaveEntries] = useState<LeaveEntry[]>([]);
  const [editEntry, setEditEntry] = useState<TimeEntry | null>(null);
  const [daySchedules, setDaySchedules] = useState<ScheduleEntry[]>([]);
  const [pauzeRegelsOpen, setPauzeRegelsOpen] = useState(false);
  const [exportPreviewOpen, setExportPreviewOpen] = useState(false);
  const [potjeSaldoMap, setPotjeSaldoMap] = useState<Map<string, number>>(new Map());

  const weekDates = useMemo(() => getWeekDates(weekStart), [weekStart]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  // Fetch data based on view mode
  useEffect(() => {
    if (viewMode === 'periode') {
      fetchTimeEntries({ from: currentPeriod.from, to: currentPeriod.to });
      fetchLeave({ from: currentPeriod.from, to: currentPeriod.to });
    } else if (viewMode === 'week') {
      const from = weekDates[0]!;
      const to = weekDates[6]!;
      fetchTimeEntries({ from, to });
      fetchLeave({ from, to });
    } else {
      fetchTimeEntries({ from: selectedDate, to: selectedDate });
      fetchLeave({ from: selectedDate, to: selectedDate });
    }
  }, [viewMode, currentPeriod, weekDates, selectedDate, fetchTimeEntries, fetchLeave]);

  // Fetch schedule for Dag-view
  useEffect(() => {
    if (viewMode !== 'dag') return;
    const fetchDaySchedule = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const res = await fetch(`/api/workforce/schedule?from=${selectedDate}&to=${selectedDate}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (res.ok) setDaySchedules(await res.json());
      } catch { /* ignore */ }
    };
    fetchDaySchedule();
  }, [viewMode, selectedDate]);

  const scheduleByStaff = useMemo(() => {
    const map = new Map<string, ScheduleEntry>();
    for (const s of daySchedules) if (s.staff_id) map.set(s.staff_id, s);
    return map;
  }, [daySchedules]);

  const handleShiftSaved = (staffId: string, result: { shiftType: ShiftTypeValue | null; scheduleId: string | null }) => {
    setDaySchedules(prev => {
      const existing = prev.find(s => s.staff_id === staffId);
      if (existing && result.scheduleId) {
        return prev.map(s =>
          s.staff_id === staffId ? { ...s, shift_type: result.shiftType, id: result.scheduleId! } : s
        );
      }
      if (!existing && result.scheduleId && result.shiftType) {
        return [...prev, { id: result.scheduleId, staff_id: staffId, date: selectedDate, shift_type: result.shiftType, time_from: '07:00', time_to: '16:00', salesforce_route_id: null, notes: null }];
      }
      return prev;
    });
  };

  const navigatePeriod = (dir: -1 | 1) => {
    const d = new Date(currentPeriod.from + 'T00:00:00');
    d.setMonth(d.getMonth() + dir);
    setCurrentPeriod(getPayPeriod(d));
  };

  const navigateWeek = (dir: -1 | 1) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(d);
  };

  const navigateDay = (dir: -1 | 1) => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + dir);
    setSelectedDate(fmtDate(d));
  };

  const activeStaff = useMemo(
    () => staffList.filter(s => s.is_active).sort((a, b) => a.name.localeCompare(b.name)),
    [staffList]
  );

  // Fetch year data for jaarsaldo (separate from period data to avoid state conflict)
  const currentYear = useMemo(() => {
    const d = new Date(currentPeriod.from + 'T00:00:00');
    return d.getMonth() >= 11 ? d.getFullYear() + 1 : d.getFullYear();
  }, [currentPeriod]);

  useEffect(() => {
    if (viewMode !== 'periode') return;
    const yearFrom = `${currentYear}-01-01`;
    const yearTo = `${currentYear}-12-31`;
    const fetchYearData = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const headers = { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' };
        const [teRes, leRes] = await Promise.all([
          fetch(`/api/workforce/time-entries?from=${yearFrom}&to=${yearTo}`, { headers }),
          fetch(`/api/workforce/leave?from=${yearFrom}&to=${yearTo}`, { headers }),
        ]);
        if (teRes.ok) setYearTimeEntries(await teRes.json());
        if (leRes.ok) setYearLeaveEntries(await leRes.json());
      } catch { /* ignore */ }
    };
    fetchYearData();
    activeStaff.forEach(s => {
      if (s.contract_type === 'vast' && s.werkrooster?.length) {
        fetchEntitlements(s.id);
      }
    });
  }, [viewMode, currentYear, activeStaff.length]);

  // ── Potje-saldo per staff (uit urensaldo_mutations) ────────────
  useEffect(() => {
    if (viewMode !== 'periode' || activeStaff.length === 0) return;
    const fetchPotjes = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const map = new Map<string, number>();
        const exportableStaff = activeStaff.filter(s => s.contract_type !== 'zzp' && s.contract_type !== 'dga');
        await Promise.all(
          exportableStaff.map(async s => {
            const res = await fetch(
              `/api/workforce/urensaldo-mutations?staff_id=${s.id}`,
              { headers: { Authorization: `Bearer ${session.access_token}` } },
            );
            if (!res.ok) return;
            const muts: Mutation[] = await res.json();
            map.set(s.id, calculatePotjeSaldo(muts));
          }),
        );
        setPotjeSaldoMap(map);
      } catch { /* ignore */ }
    };
    fetchPotjes();
  }, [viewMode, activeStaff.length, currentPeriod.label]);

  // ── Year saldo per staff ──────────────────────────────────────
  const yearSaldoMap = useMemo(() => {
    if (viewMode !== 'periode' || yearTimeEntries.length === 0) return new Map<string, number>();
    const map = new Map<string, number>();
    for (const staff of activeStaff) {
      if (staff.contract_type !== 'vast' || !staff.werkrooster?.length) continue;
      const ste = yearTimeEntries.filter(te => te.staff_id === staff.id);
      const sle = yearLeaveEntries.filter(le => le.staff_id === staff.id);
      const beginsaldo = leaveEntitlements
        .filter(ent => ent.staff_id === staff.id && ent.leave_type === ('urensaldo' as string))
        .reduce((sum, ent) => sum + ent.total_hours, 0);
      const result = calculateYearSaldo(
        staff.werkrooster, staff.team, currentYear,
        ste, sle.map(l => ({ date: l.date, hours: l.hours, leave_type: l.leave_type })),
        beginsaldo, staff.datum_in_dienst || staff.start_date || null,
      );
      if (result) map.set(staff.id, result.yearSaldo);
    }
    return map;
  }, [viewMode, activeStaff, yearTimeEntries, yearLeaveEntries, leaveEntitlements, currentYear]);

  // ── Saldo alerts (> ±16 uur) ────────────────────────────────
  const saldoAlerts = useMemo(() => {
    const alerts: { name: string; saldo: number }[] = [];
    for (const [staffId, saldo] of yearSaldoMap) {
      if (Math.abs(saldo) > 16) {
        const staff = activeStaff.find(s => s.id === staffId);
        if (staff) alerts.push({ name: staff.name, saldo });
      }
    }
    return alerts;
  }, [yearSaldoMap, activeStaff]);

  // ── Periode totals ────────────────────────────────────────────
  const totals = useMemo(() => {
    if (viewMode !== 'periode') return null;
    let totalWorked = 0, totalScheduled = 0, totalSaldo = 0, total30 = 0, total50 = 0, total150 = 0, totalH30 = 0, totalH50 = 0, totalH150 = 0, totalOvertime = 0, totalLeave = 0, totalSick = 0, totalBruto = 0, totalWerkdagen = 0, totalReiskosten = 0;
    for (const staff of activeStaff) {
      const isZzp = staff.contract_type === 'zzp';
      const se = timeEntries.filter(te => te.staff_id === staff.id);
      const sl = leaveEntries.filter(le => le.staff_id === staff.id);
      let worked = 0;
      let staffSurcharges = 0;
      for (const te of se) {
        if (te.total_hours) worked += te.total_hours;
        if (te.clock_in && te.clock_out && staff.uurloon) {
          const ci = toHHmm(te.clock_in), co = toHHmm(te.clock_out);
          if (ci && co) {
            const sc = calculateSurcharges(ci, co, te.date, staff.uurloon, te.break_minutes ?? 0, te.paid_break_minutes ?? 0);
            for (const b of sc.breakdown) {
              if (b.percentage === 0.30) { total30 += b.amount; totalH30 += b.hours; staffSurcharges += b.amount; }
              else if (b.percentage === 0.50) { total50 += b.amount; totalH50 += b.hours; staffSurcharges += b.amount; }
              else if (b.percentage === 1.50) { total150 += b.amount; totalH150 += b.hours; staffSurcharges += b.amount; }
            }
          }
        }
      }
      // Scheduled hours for vast only (min-max structurally works above minimum)
      const hasWerkrooster = staff.contract_type === 'vast' && staff.werkrooster && staff.werkrooster.length > 0;
      if (hasWerkrooster) {
        const scheduled = calculateScheduledHours(staff.werkrooster, staff.team, currentPeriod.from, currentPeriod.to, se, sl.map(l => ({ date: l.date, hours: l.hours, leave_type: l.leave_type })), staff.datum_in_dienst || staff.start_date || null);
        totalScheduled += scheduled.totalScheduled;
        totalSaldo += scheduled.saldo;
      }
      const overtime = calculateOvertime(worked);
      const staffLeave = filterNonSickLeave(sl).reduce((s, l) => s + l.hours, 0);
      const staffSick = filterSickLeave(sl).reduce((s, l) => s + l.hours, 0);
      totalWorked += worked;
      totalOvertime += overtime.overtimeSurplus;
      totalLeave += staffLeave;
      totalSick += staffSick;
      const staffContractUren = staff.contract_type === 'vast' && staff.uren_per_week
        ? Math.round((staff.uren_per_week * 52 / 12) * 100) / 100
        : null;
      const isVastOrMinMax = staff.contract_type === 'vast' || staff.contract_type === 'min-max';
      if (!isZzp && staff.uurloon) {
        const baseHours = staffContractUren ?? (isVastOrMinMax ? (staff.maand_norm || 173) : (overtime.paidHours + staffLeave + staffSick));
        totalBruto += baseHours * staff.uurloon + staffSurcharges;
      }
      // Werkdagen & reiskosten
      const staffWerkdagen = new Set(se.filter(te => te.total_hours && te.total_hours > 0).map(te => te.date)).size;
      totalWerkdagen += staffWerkdagen;
      totalReiskosten += staffWerkdagen * (staff.reiskostenvergoeding_per_dag || 0);
    }
    return { totalWorked, totalScheduled, totalSaldo, total30, total50, total150, totalH30, totalH50, totalH150, totalSurcharges: total30 + total50 + total150, totalOvertime, totalLeave, totalSick, totalBruto, totalWerkdagen, totalReiskosten };
  }, [viewMode, activeStaff, timeEntries, leaveEntries, currentPeriod]);

  // ── CAO sync & NMBRS export ───────────────────────────────────
  // Zelfde regels als de dagelijkse cron (api/_lib/loonkaart-sync.ts): contract → loongroep/erkende
  // jaren → CAO-uurloon. De knop is een handmatige extra run, geen eigen berekening meer.
  const handleCaoSync = async () => {
    setIsSyncing(true); setSyncResult(null);
    try {
      const result = await apiFetch('/api/cron/loonkaart-sync', { method: 'POST' }) as { bijgewerkt: number; details: string[] };
      setSyncResult({ updated: result.bijgewerkt, details: result.details });
      await fetchStaff(true);
    } catch (err) {
      setSyncResult({ updated: 0, details: [`Sync mislukt: ${err instanceof Error ? err.message : String(err)}`] });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleExport = () => setExportPreviewOpen(true);

  const isLoading = isLoadingEntries || isLoadingStaff;

  // ── Week view: hours per staff per day ─────────────────────────
  const weekTotals = useMemo(() => {
    if (viewMode !== 'week') return { staffRows: [], dayTotals: [] as number[], grandTotal: 0 };
    const staffRows = activeStaff.map(staff => {
      const dailyHours = weekDates.map(date => {
        const entries = timeEntries.filter(te => te.staff_id === staff.id && te.date === date);
        return entries.reduce((sum, te) => sum + (te.total_hours || 0), 0);
      });
      const weekTotal = dailyHours.reduce((a, b) => a + b, 0);
      return { staff, dailyHours, weekTotal };
    }).filter(r => r.weekTotal > 0 || timeEntries.some(te => te.staff_id === r.staff.id));

    const dayTotals = weekDates.map((_, i) => staffRows.reduce((sum, r) => sum + r.dailyHours[i]!, 0));
    const grandTotal = dayTotals.reduce((a, b) => a + b, 0);
    return { staffRows, dayTotals, grandTotal };
  }, [viewMode, activeStaff, timeEntries, weekDates]);

  // ── Day view: detailed entries ─────────────────────────────────
  const dayEntries = useMemo(() => {
    if (viewMode !== 'dag') return [];
    return activeStaff.map(staff => {
      const entries = timeEntries
        .filter(te => te.staff_id === staff.id && te.date === selectedDate)
        .sort((a, b) => (a.clock_in || '').localeCompare(b.clock_in || ''));
      const leaves = leaveEntries.filter(le => le.staff_id === staff.id && le.date === selectedDate);
      const totalHours = entries.reduce((sum, te) => sum + (te.total_hours || 0), 0);
      const totalBreak = entries.reduce((sum, te) => sum + (te.break_minutes || 0), 0);
      return { staff, entries, leaves, totalHours, totalBreak };
    }).filter(r => r.entries.length > 0 || r.leaves.length > 0);
  }, [viewMode, activeStaff, timeEntries, leaveEntries, selectedDate]);

  const weekNumber = getWeek(weekStart, { weekStartsOn: 1 });
  const weekLabel = `Week ${weekNumber} — ${format(weekStart, 'd MMM', { locale: nl })} t/m ${format(addDays(weekStart, 6), 'd MMM yyyy', { locale: nl })}`;
  const dayLabel = format(parseISO(selectedDate), 'EEEE d MMMM yyyy', { locale: nl });

  return (
    <>
      <div className="p-6 max-w-full mx-auto overflow-y-auto h-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-heading font-semibold text-main">Uren</h1>
            <p className="text-muted text-sm">
              {viewMode === 'periode' && 'Loonperiode 20e — 19e · CAO Schoonmaak'}
              {viewMode === 'week' && 'Weekoverzicht geklokte uren'}
              {viewMode === 'dag' && 'Dagoverzicht klokregistraties'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPauzeRegelsOpen(true)}
              className="px-3 py-2 border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50 transition-colors flex items-center gap-1.5"
              title="Hoe wordt pauze berekend?"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Pauze-regels
            </button>
            {viewMode === 'periode' && (
              <>
                <button onClick={handleExport} className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  NMBRS
                </button>
                <button onClick={handleCaoSync} disabled={isSyncing} className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                  {isSyncing ? 'Bijwerken...' : 'CAO uurlonen'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* View mode toggle + navigation */}
        <div className="flex flex-wrap items-center gap-4 mb-5">
          {/* View toggle */}
          <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden">
            {([['periode', 'Periode'], ['week', 'Week'], ['dag', 'Dag']] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setViewMode(key)}
                className={`px-4 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === key ? 'bg-indigo-500 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Navigation per view */}
          {viewMode === 'periode' && (
            <div className="flex items-center gap-2">
              <button onClick={() => navigatePeriod(-1)} className="p-2 hover:bg-slate-100 rounded-lg"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg></button>
              <span className="text-sm font-medium text-main min-w-[260px] text-center capitalize">{currentPeriod.label} — {currentPeriod.from.slice(8)}-{currentPeriod.from.slice(5,7)} t/m {currentPeriod.to.slice(8)}-{currentPeriod.to.slice(5,7)}</span>
              <button onClick={() => navigatePeriod(1)} className="p-2 hover:bg-slate-100 rounded-lg"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg></button>
              <button onClick={() => setCurrentPeriod(getPayPeriod(new Date()))} className="px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg">Huidige periode</button>
            </div>
          )}
          {viewMode === 'week' && (
            <div className="flex items-center gap-2">
              <button onClick={() => navigateWeek(-1)} className="p-2 hover:bg-slate-100 rounded-lg"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg></button>
              <span className="text-sm font-medium text-main min-w-[280px] text-center">{weekLabel}</span>
              <button onClick={() => navigateWeek(1)} className="p-2 hover:bg-slate-100 rounded-lg"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg></button>
              <button onClick={() => setWeekStart(getMonday(new Date()))} className="px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg">Deze week</button>
            </div>
          )}
          {viewMode === 'dag' && (
            <div className="flex items-center gap-2">
              <button onClick={() => navigateDay(-1)} className="p-2 hover:bg-slate-100 rounded-lg"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg></button>
              <span className="text-sm font-medium text-main min-w-[240px] text-center capitalize">{dayLabel}</span>
              <button onClick={() => navigateDay(1)} className="p-2 hover:bg-slate-100 rounded-lg"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg></button>
              <button onClick={() => setSelectedDate(fmtDate(new Date()))} className="px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg">Vandaag</button>
            </div>
          )}
        </div>

        {/* CAO sync result */}
        {syncResult && (
          <div className={`mb-4 p-4 rounded-lg border ${syncResult.updated > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
            <p className="text-sm font-medium text-main">{syncResult.updated > 0 ? `${syncResult.updated} uurlonen bijgewerkt` : 'Alle uurlonen zijn actueel'}</p>
            {syncResult.details.length > 0 && <ul className="mt-2 space-y-1">{syncResult.details.map((d, i) => <li key={i} className="text-sm text-muted">{d}</li>)}</ul>}
            <button onClick={() => setSyncResult(null)} className="mt-2 text-xs text-muted hover:text-main">Sluiten</button>
          </div>
        )}

        {/* ═══ PERIODE VIEW ═══ */}
        {viewMode === 'periode' && totals && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
              <Card variant="elevated" className="p-4"><p className="text-xs text-muted">Totaal gewerkt</p><p className="text-xl font-semibold text-main">{formatHours(totals.totalWorked)}</p></Card>
              <Card variant="elevated" className="p-4"><p className="text-xs text-muted">Brutoloon</p><p className="text-xl font-semibold text-main">{formatEuro(totals.totalBruto)}</p></Card>
              <Card variant="elevated" className="p-4"><p className="text-xs text-muted">Toeslagen</p><p className="text-xl font-semibold text-emerald-600">{formatEuro(totals.totalSurcharges)}</p></Card>
              <Card variant="elevated" className="p-4"><p className="text-xs text-muted">Overuren (potje)</p><p className="text-xl font-semibold text-indigo-600">{formatHours(totals.totalOvertime)}</p></Card>
              <Card variant="elevated" className="p-4"><p className="text-xs text-muted">Verlof</p><p className="text-xl font-semibold text-amber-600">{formatHours(totals.totalLeave)}</p></Card>
              <Card variant="elevated" className="p-4"><p className="text-xs text-muted">Ziekte</p><p className="text-xl font-semibold text-red-600">{formatHours(totals.totalSick)}</p></Card>
            </div>

            {/* Saldo alerts */}
            {saldoAlerts.length > 0 && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-semibold text-amber-800 mb-1">Let op: afwijkend jaarsaldo</p>
                {saldoAlerts.map(a => (
                  <p key={a.name} className="text-sm text-amber-700">
                    <span className="font-medium">{a.name}</span> staat op{' '}
                    <span className={`font-semibold ${a.saldo >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {a.saldo >= 0 ? '+' : ''}{formatHours(a.saldo)}
                    </span>
                    {' '}jaarsaldo.{a.saldo < -16 ? ' Overweeg extra uren in te roosteren.' : ' Overweeg uren af te bouwen.'}
                  </p>
                ))}
              </div>
            )}

            <Card variant="elevated" padding="none">
              <div className="overflow-x-auto">
                {isLoading ? (
                  <div className="flex justify-center py-12"><Spinner /></div>
                ) : (
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="px-5 py-3 text-xs font-medium text-muted uppercase">Medewerker</th>
                        <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Verwacht</th>
                        <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Gewerkt</th>
                        <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Verlof</th>
                        <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Ziekte</th>
                        <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Saldo</th>
                        <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right" title="Overurenpotje — cumulatieve mutaties (CAO 173u-drempel). Rooster-delta staat in alerts bovenin.">Potje</th>
                        <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Brutoloon</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {activeStaff.map(staff => (
                        <PeriodeStaffRow key={staff.id} staff={staff}
                          timeEntries={timeEntries.filter(te => te.staff_id === staff.id)}
                          leaveEntries={leaveEntries.filter(le => le.staff_id === staff.id)}
                          period={currentPeriod}
                          potjeSaldo={potjeSaldoMap.get(staff.id) ?? null}
                          onClick={() => navigate(`/personeel/${staff.id}`)} />
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-200 bg-gray-50">
                        <td className="px-5 py-3 font-semibold text-main">Totaal</td>
                        <td className="px-3 py-3 text-right font-semibold text-muted">{totals.totalScheduled > 0 ? formatHours(totals.totalScheduled) : '-'}</td>
                        <td className="px-3 py-3 text-right font-semibold text-main">{formatHours(totals.totalWorked)}</td>
                        <td className="px-3 py-3 text-right font-semibold text-amber-600">{formatHours(totals.totalLeave)}</td>
                        <td className="px-3 py-3 text-right font-semibold text-red-600">{formatHours(totals.totalSick)}</td>
                        <td className="px-3 py-3 text-right">
                          {totals.totalScheduled > 0 ? (
                            <span className={`font-semibold ${totals.totalSaldo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {totals.totalSaldo >= 0 ? '+' : ''}{formatHours(totals.totalSaldo)}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-3 py-3" />
                        <td className="px-3 py-3 text-right font-semibold text-main">{formatEuro(totals.totalBruto)}</td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </Card>
          </>
        )}

        {/* ═══ WEEK VIEW ═══ */}
        {viewMode === 'week' && (
          <Card variant="elevated" padding="none">
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="flex justify-center py-12"><Spinner /></div>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="sticky left-0 z-10 bg-gray-50 px-5 py-3 text-xs font-medium text-muted uppercase w-[160px]">Medewerker</th>
                      {weekDates.map(date => {
                        const isToday = date === fmtDate(new Date());
                        return (
                          <th key={date} className={`px-2 py-3 text-center text-xs font-medium uppercase min-w-[80px] ${isToday ? 'bg-indigo-50 text-indigo-700' : 'text-muted'}`}>
                            <div>{format(parseISO(date), 'EEE', { locale: nl })}</div>
                            <div className="text-[10px]">{format(parseISO(date), 'd MMM', { locale: nl })}</div>
                          </th>
                        );
                      })}
                      <th className="px-3 py-3 text-center text-xs font-medium text-muted uppercase bg-gray-100 w-[70px]">Totaal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {weekTotals.staffRows.length === 0 ? (
                      <tr><td colSpan={9} className="text-center text-sm text-muted py-8">Geen uren geregistreerd deze week.</td></tr>
                    ) : (
                      weekTotals.staffRows.map(({ staff, dailyHours, weekTotal }) => (
                        <tr key={staff.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/personeel/${staff.id}`)}>
                          <td className="sticky left-0 z-10 bg-white px-5 py-3 border-r border-gray-100">
                            <p className="font-medium text-main text-sm">{staff.name}</p>
                            <p className="text-[10px] text-muted capitalize">{staff.team || '-'}</p>
                          </td>
                          {dailyHours.map((hours, i) => {
                            const isToday = weekDates[i] === fmtDate(new Date());
                            const leave = leaveEntries.find(le => le.staff_id === staff.id && le.date === weekDates[i]);
                            return (
                              <td key={i} className={`px-2 py-3 text-center ${isToday ? 'bg-indigo-50/30' : ''}`}>
                                {leave ? (
                                  <span className={`text-xs font-medium ${leave.leave_type === 'ziekte' ? 'text-red-500' : 'text-amber-500'}`}>
                                    {leave.leave_type === 'ziekte' ? 'Ziek' : 'Verlof'}
                                  </span>
                                ) : hours > 0 ? (
                                  <span className="text-sm font-semibold text-main">{formatHours(hours)}</span>
                                ) : (
                                  <span className="text-xs text-slate-300">-</span>
                                )}
                              </td>
                            );
                          })}
                          <td className="px-3 py-3 text-center bg-gray-50/50">
                            <span className="text-sm font-bold text-main">{weekTotal > 0 ? formatHours(weekTotal) : '-'}</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {weekTotals.staffRows.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-gray-200 bg-gray-50">
                        <td className="sticky left-0 z-10 bg-gray-50 px-5 py-3 font-semibold text-main">Totaal</td>
                        {weekTotals.dayTotals.map((total, i) => (
                          <td key={i} className="px-2 py-3 text-center font-semibold text-main text-sm">{total > 0 ? formatHours(total) : '-'}</td>
                        ))}
                        <td className="px-3 py-3 text-center font-bold text-main bg-gray-100">{formatHours(weekTotals.grandTotal)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}
            </div>
          </Card>
        )}

        <TimeEntryEditDialog entry={editEntry} onClose={() => setEditEntry(null)} />
        <PauzeRegelsModal open={pauzeRegelsOpen} onClose={() => setPauzeRegelsOpen(false)} />
        <NmbrsExportPreview
          open={exportPreviewOpen}
          onClose={() => setExportPreviewOpen(false)}
          period={currentPeriod}
          activeStaff={activeStaff}
          timeEntries={timeEntries}
          leaveEntries={leaveEntries}
          yearSaldoMap={yearSaldoMap}
          onExported={() => {
            // Refresh period data after export
            fetchTimeEntries({ from: currentPeriod.from, to: currentPeriod.to });
            fetchLeave({ from: currentPeriod.from, to: currentPeriod.to });
          }}
        />

        {/* ═══ DAG VIEW ═══ */}
        {viewMode === 'dag' && (
          <Card variant="elevated" padding="none">
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="flex justify-center py-12"><Spinner /></div>
              ) : dayEntries.length === 0 ? (
                <p className="text-center text-sm text-muted py-12">Geen registraties op {dayLabel}.</p>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-5 py-3 text-xs font-medium text-muted uppercase">Medewerker</th>
                      <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-center">Type</th>
                      <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-center">In</th>
                      <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-center">Uit</th>
                      <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-center">Pauze</th>
                      <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Bruto</th>
                      <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Netto</th>
                      <th className="px-3 py-3 text-xs font-medium text-muted uppercase">Opmerking</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {dayEntries.map(({ staff, entries, leaves }) => (
                      entries.length > 0 ? entries.map((te, idx) => {
                        const clockIn = toHHmm(te.clock_in);
                        const clockOut = toHHmm(te.clock_out);
                        const brutoMinutes = clockIn && clockOut
                          ? (() => {
                              const [h1, m1] = clockIn.split(':').map(Number);
                              const [h2, m2] = clockOut.split(':').map(Number);
                              return (h2! * 60 + m2!) - (h1! * 60 + m1!);
                            })()
                          : null;
                        const brutoHours = brutoMinutes ? brutoMinutes / 60 : null;
                        const scheduleEntry = scheduleByStaff.get(staff.id);
                        return (
                          <tr
                            key={te.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => setEditEntry(te)}
                          >
                            {idx === 0 && (
                              <td className="px-5 py-3" rowSpan={entries.length}>
                                <p className="font-medium text-main">{staff.name}</p>
                                <p className="text-[10px] text-muted capitalize">{staff.team || '-'}</p>
                              </td>
                            )}
                            {idx === 0 && (
                              <td className="px-3 py-3 text-center" rowSpan={entries.length}>
                                <ShiftTypeSelector
                                  staffId={staff.id}
                                  date={selectedDate}
                                  currentShift={(scheduleEntry?.shift_type as ShiftTypeValue | null | undefined) ?? null}
                                  scheduleId={scheduleEntry?.id ?? null}
                                  defaultTimeFrom={clockIn ?? '07:00'}
                                  defaultTimeTo={clockOut ?? '16:00'}
                                  onSaved={result => handleShiftSaved(staff.id, result)}
                                />
                              </td>
                            )}
                            <td className="px-3 py-3 text-center text-sm text-main font-mono">{clockIn || '-'}</td>
                            <td className="px-3 py-3 text-center text-sm text-main font-mono">{clockOut || <span className="text-amber-500 text-xs">actief</span>}</td>
                            <td className="px-3 py-3 text-center text-sm text-muted">
                              {te.break_minutes ? `${te.break_minutes}m` : '-'}
                              <BreakSourceDot source={te.break_source} />
                            </td>
                            <td className="px-3 py-3 text-right text-sm text-muted">{brutoHours ? formatHours(brutoHours) : '-'}</td>
                            <td className="px-3 py-3 text-right text-sm font-semibold text-main">{te.total_hours ? formatHours(te.total_hours) : '-'}</td>
                            <td className="px-3 py-3 text-xs text-muted">{te.notes || ''}</td>
                          </tr>
                        );
                      }) : (
                        <tr key={staff.id} className="hover:bg-gray-50">
                          <td className="px-5 py-3">
                            <p className="font-medium text-main">{staff.name}</p>
                            <p className="text-[10px] text-muted capitalize">{staff.team || '-'}</p>
                          </td>
                          <td colSpan={7} className="px-3 py-3">
                            {leaves.map(l => (
                              <span key={l.id} className={`text-xs font-medium px-2 py-1 rounded ${l.leave_type === 'ziekte' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`}>
                                {l.leave_type === 'ziekte' ? 'Ziek' : 'Verlof'} ({formatHours(l.hours)})
                              </span>
                            ))}
                          </td>
                        </tr>
                      )
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 bg-gray-50">
                      <td className="px-5 py-3 font-semibold text-main">Totaal ({dayEntries.filter(d => d.entries.length > 0).length} medewerkers)</td>
                      <td colSpan={4} />
                      <td className="px-3 py-3" />
                      <td className="px-3 py-3 text-right font-bold text-main">
                        {formatHours(dayEntries.reduce((sum, d) => sum + d.totalHours, 0))}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
